import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "@/app/store";
import {
  onboardingBAlertTimingOptions,
  onboardingBGrowthWindowOptions,
  onboardingBStartMethodOptions,
  onboardingCPriceRangeOptions,
  onboardingCPrimaryGoalOptions,
  onboardingCProductStageOptions,
  onboardingDContentGoalOptions,
  onboardingDEntryStateOptions,
  onboardingMarketIds,
  type OnboardingBAlertTimingId,
  type OnboardingBGrowthWindowId,
  type OnboardingBStartMethodId,
  type OnboardingCPriceRangeId,
  type OnboardingCPrimaryGoalId,
  type OnboardingCProductStageId,
  type OnboardingDContentGoalId,
  type OnboardingDEntryStateId,
} from "@/features/onboarding/onboardingCatalog";

export const onboardingProfileIds = [
  "fast-supply",
  "follow-opportunity",
  "brand-product",
  "undecided",
] as const;

export type OnboardingProfileId = (typeof onboardingProfileIds)[number];

const concreteOnboardingProfileIds = onboardingProfileIds.slice(0, 3) as readonly Exclude<
  OnboardingProfileId,
  "undecided"
>[];

export const onboardingShopStatusIds = [
  "selling",
  "store-ready",
  "preparing",
  "multi-store",
] as const;

export type OnboardingShopStatusId =
  (typeof onboardingShopStatusIds)[number];

export const onboardingQuestionIds = [
  "profile-priority",
  "shop-status",
  "markets",
  "categories",
  "a-advantages",
  "b-start-method",
  "b-alert-timing",
  "b-growth-window",
  "c-product-stage",
  "c-advantages",
  "c-price-range",
  "c-primary-goal",
  "d-entry-state",
  "d-content-goal",
] as const;

export type OnboardingQuestionId = (typeof onboardingQuestionIds)[number];

export interface OnboardingRouteContext {
  primaryProfileId: OnboardingProfileId | null;
  selectedProfileIds: readonly OnboardingProfileId[];
}

const primaryQuestionSequences = {
  "brand-product": [
    "c-product-stage",
    "markets",
    "categories",
    "c-advantages",
    "c-price-range",
    "c-primary-goal",
  ],
  "fast-supply": [
    "shop-status",
    "markets",
    "categories",
    "a-advantages",
  ],
  "follow-opportunity": [
    "shop-status",
    "markets",
    "categories",
    "b-start-method",
    "b-alert-timing",
    "b-growth-window",
  ],
  undecided: [
    "d-entry-state",
    "markets",
    "categories",
    "d-content-goal",
  ],
} as const satisfies Record<
  OnboardingProfileId,
  readonly OnboardingQuestionId[]
>;

const supplementalQuestionSequences = {
  "brand-product": [
    "c-product-stage",
    "c-advantages",
    "c-price-range",
    "c-primary-goal",
  ],
  "fast-supply": ["a-advantages"],
  "follow-opportunity": [
    "b-start-method",
    "b-alert-timing",
    "b-growth-window",
  ],
} as const satisfies Record<
  Exclude<OnboardingProfileId, "undecided">,
  readonly OnboardingQuestionId[]
>;

export interface OnboardingAAnswersState {
  advantageIds: string[];
  otherAdvantage: string;
}

export interface OnboardingBAnswersState {
  alertTiming: OnboardingBAlertTimingId | null;
  growthWindow: OnboardingBGrowthWindowId | null;
  startMethod: OnboardingBStartMethodId | null;
}

export interface OnboardingCAnswersState {
  advantageIds: string[];
  otherAdvantage: string;
  priceRange: OnboardingCPriceRangeId | null;
  primaryGoal: OnboardingCPrimaryGoalId | null;
  productStage: OnboardingCProductStageId | null;
}

export interface OnboardingDAnswersState {
  contentGoal: OnboardingDContentGoalId | null;
  entryState: OnboardingDEntryStateId | null;
}

export interface OnboardingQuestionFlowState {
  a: OnboardingAAnswersState;
  b: OnboardingBAnswersState;
  c: OnboardingCAnswersState;
  categoryIds: string[];
  completedQuestionIds: OnboardingQuestionId[];
  d: OnboardingDAnswersState;
  marketIds: string[];
  otherCategory: string;
  shopStatus: OnboardingShopStatusId | null;
}

export interface OnboardingState {
  primaryProfileId: OnboardingProfileId | null;
  questionFlow: OnboardingQuestionFlowState;
  selectedProfileIds: OnboardingProfileId[];
}

function createInitialAAnswers(): OnboardingAAnswersState {
  return { advantageIds: [], otherAdvantage: "" };
}

function createInitialBAnswers(): OnboardingBAnswersState {
  return { alertTiming: null, growthWindow: null, startMethod: null };
}

function createInitialCAnswers(): OnboardingCAnswersState {
  return {
    advantageIds: [],
    otherAdvantage: "",
    priceRange: null,
    primaryGoal: null,
    productStage: null,
  };
}

function createInitialDAnswers(): OnboardingDAnswersState {
  return { contentGoal: null, entryState: null };
}

function createInitialQuestionFlow(): OnboardingQuestionFlowState {
  return {
    a: createInitialAAnswers(),
    b: createInitialBAnswers(),
    c: createInitialCAnswers(),
    categoryIds: [],
    completedQuestionIds: [],
    d: createInitialDAnswers(),
    marketIds: [],
    otherCategory: "",
    shopStatus: null,
  };
}

function createInitialState(): OnboardingState {
  return {
    primaryProfileId: null,
    questionFlow: createInitialQuestionFlow(),
    selectedProfileIds: [],
  };
}

const initialState = createInitialState();
const onboardingProfileIdSet = new Set<string>(onboardingProfileIds);
const onboardingShopStatusIdSet = new Set<string>(onboardingShopStatusIds);
const onboardingMarketIdSet = new Set<string>(onboardingMarketIds);
const onboardingBStartMethodIdSet = new Set<string>(
  onboardingBStartMethodOptions.map((option) => option.id),
);
const onboardingBAlertTimingIdSet = new Set<string>(
  onboardingBAlertTimingOptions.map((option) => option.id),
);
const onboardingBGrowthWindowIdSet = new Set<string>(
  onboardingBGrowthWindowOptions.map((option) => option.id),
);
const onboardingCProductStageIdSet = new Set<string>(
  onboardingCProductStageOptions.map((option) => option.id),
);
const onboardingCPriceRangeIdSet = new Set<string>(
  onboardingCPriceRangeOptions.map((option) => option.id),
);
const onboardingCPrimaryGoalIdSet = new Set<string>(
  onboardingCPrimaryGoalOptions.map((option) => option.id),
);
const onboardingDEntryStateIdSet = new Set<string>(
  onboardingDEntryStateOptions.map((option) => option.id),
);
const onboardingDContentGoalIdSet = new Set<string>(
  onboardingDContentGoalOptions.map((option) => option.id),
);

function stableUnique<TValue>(values: readonly TValue[]) {
  return [...new Set(values)];
}

function normalizeProfileIds(profileIds: readonly string[]) {
  const requestedIds = new Set(
    profileIds.filter((profileId) => onboardingProfileIdSet.has(profileId)),
  );

  return onboardingProfileIds.filter((profileId) => requestedIds.has(profileId));
}

export function normalizeOnboardingProfileSelection(
  profileIds: readonly string[],
  previousProfileIds: readonly OnboardingProfileId[] = [],
): OnboardingProfileId[] {
  const normalizedIds = normalizeProfileIds(profileIds);
  const hasUndecided = normalizedIds.includes("undecided");

  if (!hasUndecided) {
    return normalizedIds;
  }

  if (!previousProfileIds.includes("undecided")) {
    return ["undecided"];
  }

  const concreteIds = normalizedIds.filter(
    (profileId) => profileId !== "undecided",
  );

  return concreteIds.length > 0 ? concreteIds : ["undecided"];
}

function resolvePrimaryProfileId({
  primaryProfileId,
  selectedProfileIds,
}: OnboardingRouteContext) {
  const normalizedIds = normalizeProfileIds(selectedProfileIds);

  if (normalizedIds.length === 0) {
    return primaryProfileId === "undecided" ? "undecided" : null;
  }

  if (normalizedIds.length === 1) {
    return normalizedIds[0];
  }

  if (primaryProfileId === "undecided") {
    return primaryProfileId;
  }

  return primaryProfileId && normalizedIds.includes(primaryProfileId)
    ? primaryProfileId
    : null;
}

export function getOnboardingQuestionSequence({
  primaryProfileId,
  selectedProfileIds,
}: OnboardingRouteContext): readonly OnboardingQuestionId[] {
  const normalizedIds = normalizeProfileIds(selectedProfileIds);
  const resolvedPrimaryId = resolvePrimaryProfileId({
    primaryProfileId,
    selectedProfileIds: normalizedIds,
  });
  const sequence: OnboardingQuestionId[] = [];

  if (normalizedIds.length > 1) {
    sequence.push("profile-priority");
  }

  if (!resolvedPrimaryId) {
    return sequence;
  }

  sequence.push(...primaryQuestionSequences[resolvedPrimaryId]);

  for (const profileId of concreteOnboardingProfileIds) {
    if (normalizedIds.includes(profileId) && profileId !== resolvedPrimaryId) {
      sequence.push(...supplementalQuestionSequences[profileId]);
    }
  }

  return stableUnique(sequence);
}

export function usesNoStoreMarketCopy(
  state: OnboardingQuestionFlowState,
  primaryProfileId: OnboardingProfileId | null,
) {
  if (primaryProfileId === "brand-product") {
    return true;
  }

  if (
    primaryProfileId === "fast-supply" ||
    primaryProfileId === "follow-opportunity"
  ) {
    return state.shopStatus === "preparing";
  }

  if (primaryProfileId !== "undecided") {
    return false;
  }

  return (
    state.d.entryState === "explore-opportunities" ||
    state.d.entryState === "considering-store"
  );
}

function normalizeIds(ids: readonly string[], allowedIds?: ReadonlySet<string>) {
  const uniqueIds = new Set(
    allowedIds ? ids.filter((id) => allowedIds.has(id)) : ids,
  );

  return [...uniqueIds];
}

function normalizeChoiceId<TId extends string>(
  value: string | null,
  allowedIds: ReadonlySet<string>,
) {
  return value !== null && allowedIds.has(value) ? (value as TId) : null;
}

function getRouteContext(state: OnboardingState): OnboardingRouteContext {
  return {
    primaryProfileId: state.primaryProfileId,
    selectedProfileIds: state.selectedProfileIds,
  };
}

function getSequence(state: OnboardingState) {
  return getOnboardingQuestionSequence(getRouteContext(state));
}

function isReachable(state: OnboardingState, questionId: OnboardingQuestionId) {
  return getSequence(state).includes(questionId);
}

function clearQuestionAnswer(
  state: OnboardingQuestionFlowState,
  questionId: OnboardingQuestionId,
) {
  switch (questionId) {
    case "profile-priority":
      return;
    case "shop-status":
      state.shopStatus = null;
      return;
    case "markets":
      state.marketIds = [];
      return;
    case "categories":
      state.categoryIds = [];
      state.otherCategory = "";
      return;
    case "a-advantages":
      state.a = createInitialAAnswers();
      return;
    case "b-start-method":
      state.b.startMethod = null;
      return;
    case "b-alert-timing":
      state.b.alertTiming = null;
      return;
    case "b-growth-window":
      state.b.growthWindow = null;
      return;
    case "c-product-stage":
      state.c.productStage = null;
      return;
    case "c-advantages":
      state.c.advantageIds = [];
      state.c.otherAdvantage = "";
      return;
    case "c-price-range":
      state.c.priceRange = null;
      return;
    case "c-primary-goal":
      state.c.primaryGoal = null;
      return;
    case "d-entry-state":
      state.d.entryState = null;
      return;
    case "d-content-goal":
      state.d.contentGoal = null;
  }
}

function invalidateFrom(state: OnboardingState, questionId: OnboardingQuestionId) {
  const sequence = getSequence(state);
  const questionIndex = sequence.indexOf(questionId);

  if (questionIndex < 0) {
    return;
  }

  for (const downstreamId of sequence.slice(questionIndex + 1)) {
    clearQuestionAnswer(state.questionFlow, downstreamId);
  }

  state.questionFlow.completedQuestionIds =
    state.questionFlow.completedQuestionIds.filter(
      (completedId) => sequence.indexOf(completedId) < questionIndex,
    );
}

function getPotentialSequence(state: OnboardingState) {
  if (state.selectedProfileIds.length <= 1 || state.primaryProfileId !== null) {
    return getSequence(state);
  }

  const potentialQuestions: OnboardingQuestionId[] = ["profile-priority"];

  for (const profileId of state.selectedProfileIds) {
    potentialQuestions.push(...primaryQuestionSequences[profileId]);
  }

  return stableUnique(potentialQuestions);
}

function reconcileCompletedQuestions(
  state: OnboardingState,
  sequence: readonly OnboardingQuestionId[],
) {
  const completedIds = new Set(state.questionFlow.completedQuestionIds);
  state.questionFlow.completedQuestionIds = sequence.filter((questionId) =>
    completedIds.has(questionId),
  );
}

function reconcileIdentityState(state: OnboardingState) {
  const selectedIds = new Set(state.selectedProfileIds);

  if (!selectedIds.has("fast-supply")) {
    state.questionFlow.a = createInitialAAnswers();
  }
  if (!selectedIds.has("follow-opportunity")) {
    state.questionFlow.b = createInitialBAnswers();
  }
  if (!selectedIds.has("brand-product")) {
    state.questionFlow.c = createInitialCAnswers();
  }
  if (state.primaryProfileId !== "undecided") {
    state.questionFlow.d = createInitialDAnswers();
  }

  const potentialSequence = getPotentialSequence(state);

  if (!potentialSequence.includes("shop-status")) {
    state.questionFlow.shopStatus = null;
  }
  if (!potentialSequence.includes("d-entry-state")) {
    state.questionFlow.d = createInitialDAnswers();
  }

  reconcileCompletedQuestions(state, potentialSequence);
}

const onboardingSlice = createSlice({
  initialState,
  name: "onboarding",
  reducers: {
    completeOnboardingQuestion: (
      state,
      action: PayloadAction<OnboardingQuestionId>,
    ) => {
      const sequence = getSequence(state);
      const questionIndex = sequence.indexOf(action.payload);

      if (questionIndex < 0) {
        return;
      }

      const canComplete = sequence
        .slice(0, questionIndex)
        .every((questionId) =>
          state.questionFlow.completedQuestionIds.includes(questionId),
        );

      if (
        canComplete &&
        !state.questionFlow.completedQuestionIds.includes(action.payload)
      ) {
        state.questionFlow.completedQuestionIds.push(action.payload);
      }
    },
    hydrateOnboarding: (_state, action: PayloadAction<OnboardingState>) =>
      action.payload,
    resetOnboarding: () => createInitialState(),
    setOnboardingAAdvantageIds: (
      state,
      action: PayloadAction<readonly string[]>,
    ) => {
      if (!isReachable(state, "a-advantages")) {
        return;
      }

      const nextIds = normalizeIds(action.payload);
      if (
        nextIds.join("\u0000") ===
        state.questionFlow.a.advantageIds.join("\u0000")
      ) {
        return;
      }

      state.questionFlow.a.advantageIds = nextIds;
      invalidateFrom(state, "a-advantages");
    },
    setOnboardingAOtherAdvantage: (state, action: PayloadAction<string>) => {
      if (
        !isReachable(state, "a-advantages") ||
        action.payload === state.questionFlow.a.otherAdvantage
      ) {
        return;
      }

      state.questionFlow.a.otherAdvantage = action.payload;
      invalidateFrom(state, "a-advantages");
    },
    setOnboardingBAlertTiming: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      if (!isReachable(state, "b-alert-timing")) {
        return;
      }

      const nextValue = normalizeChoiceId<OnboardingBAlertTimingId>(
        action.payload,
        onboardingBAlertTimingIdSet,
      );
      if (nextValue === state.questionFlow.b.alertTiming) {
        return;
      }

      state.questionFlow.b.alertTiming = nextValue;
      invalidateFrom(state, "b-alert-timing");
    },
    setOnboardingBGrowthWindow: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      if (!isReachable(state, "b-growth-window")) {
        return;
      }

      const nextValue = normalizeChoiceId<OnboardingBGrowthWindowId>(
        action.payload,
        onboardingBGrowthWindowIdSet,
      );
      if (nextValue === state.questionFlow.b.growthWindow) {
        return;
      }

      state.questionFlow.b.growthWindow = nextValue;
      invalidateFrom(state, "b-growth-window");
    },
    setOnboardingBStartMethod: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      if (!isReachable(state, "b-start-method")) {
        return;
      }

      const nextValue = normalizeChoiceId<OnboardingBStartMethodId>(
        action.payload,
        onboardingBStartMethodIdSet,
      );
      if (nextValue === state.questionFlow.b.startMethod) {
        return;
      }

      state.questionFlow.b.startMethod = nextValue;
      invalidateFrom(state, "b-start-method");
    },
    setOnboardingCAdvantageIds: (
      state,
      action: PayloadAction<readonly string[]>,
    ) => {
      if (!isReachable(state, "c-advantages")) {
        return;
      }

      const nextIds = normalizeIds(action.payload);
      if (
        nextIds.join("\u0000") ===
        state.questionFlow.c.advantageIds.join("\u0000")
      ) {
        return;
      }

      state.questionFlow.c.advantageIds = nextIds;
      invalidateFrom(state, "c-advantages");
    },
    setOnboardingCOtherAdvantage: (state, action: PayloadAction<string>) => {
      if (
        !isReachable(state, "c-advantages") ||
        action.payload === state.questionFlow.c.otherAdvantage
      ) {
        return;
      }

      state.questionFlow.c.otherAdvantage = action.payload;
      invalidateFrom(state, "c-advantages");
    },
    setOnboardingCPriceRange: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      if (!isReachable(state, "c-price-range")) {
        return;
      }

      const nextValue = normalizeChoiceId<OnboardingCPriceRangeId>(
        action.payload,
        onboardingCPriceRangeIdSet,
      );
      if (nextValue === state.questionFlow.c.priceRange) {
        return;
      }

      state.questionFlow.c.priceRange = nextValue;
      invalidateFrom(state, "c-price-range");
    },
    setOnboardingCPrimaryGoal: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      if (!isReachable(state, "c-primary-goal")) {
        return;
      }

      const nextValue = normalizeChoiceId<OnboardingCPrimaryGoalId>(
        action.payload,
        onboardingCPrimaryGoalIdSet,
      );
      if (nextValue === state.questionFlow.c.primaryGoal) {
        return;
      }

      state.questionFlow.c.primaryGoal = nextValue;
      invalidateFrom(state, "c-primary-goal");
    },
    setOnboardingCProductStage: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      if (!isReachable(state, "c-product-stage")) {
        return;
      }

      const nextValue = normalizeChoiceId<OnboardingCProductStageId>(
        action.payload,
        onboardingCProductStageIdSet,
      );
      if (nextValue === state.questionFlow.c.productStage) {
        return;
      }

      state.questionFlow.c.productStage = nextValue;
      invalidateFrom(state, "c-product-stage");
    },
    setOnboardingCategoryIds: (
      state,
      action: PayloadAction<readonly string[]>,
    ) => {
      if (!isReachable(state, "categories")) {
        return;
      }

      const nextIds = normalizeIds(action.payload);
      if (
        nextIds.join("\u0000") === state.questionFlow.categoryIds.join("\u0000")
      ) {
        return;
      }

      state.questionFlow.categoryIds = nextIds;
      invalidateFrom(state, "categories");
    },
    setOnboardingDContentGoal: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      if (!isReachable(state, "d-content-goal")) {
        return;
      }

      const nextValue = normalizeChoiceId<OnboardingDContentGoalId>(
        action.payload,
        onboardingDContentGoalIdSet,
      );
      if (nextValue === state.questionFlow.d.contentGoal) {
        return;
      }

      state.questionFlow.d.contentGoal = nextValue;
      invalidateFrom(state, "d-content-goal");
    },
    setOnboardingDEntryState: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      if (!isReachable(state, "d-entry-state")) {
        return;
      }

      const nextValue = normalizeChoiceId<OnboardingDEntryStateId>(
        action.payload,
        onboardingDEntryStateIdSet,
      );
      if (nextValue === state.questionFlow.d.entryState) {
        return;
      }

      state.questionFlow.d.entryState = nextValue;
      invalidateFrom(state, "d-entry-state");
    },
    setOnboardingMarketIds: (
      state,
      action: PayloadAction<readonly string[]>,
    ) => {
      if (!isReachable(state, "markets")) {
        return;
      }

      const nextIds = normalizeIds(action.payload, onboardingMarketIdSet);
      if (
        nextIds.join("\u0000") === state.questionFlow.marketIds.join("\u0000")
      ) {
        return;
      }

      state.questionFlow.marketIds = nextIds;
      invalidateFrom(state, "markets");
    },
    setOnboardingOtherCategory: (state, action: PayloadAction<string>) => {
      if (
        !isReachable(state, "categories") ||
        action.payload === state.questionFlow.otherCategory
      ) {
        return;
      }

      state.questionFlow.otherCategory = action.payload;
      invalidateFrom(state, "categories");
    },
    setOnboardingPrimaryProfileId: (
      state,
      action: PayloadAction<OnboardingProfileId | null>,
    ) => {
      const requestedId = action.payload;
      const canUseRequestedId =
        requestedId === null ||
        requestedId === "undecided" ||
        state.selectedProfileIds.includes(requestedId);
      const nextPrimaryId = canUseRequestedId ? requestedId : null;

      if (nextPrimaryId === state.primaryProfileId) {
        return;
      }

      state.primaryProfileId = nextPrimaryId;
      state.questionFlow.completedQuestionIds =
        state.questionFlow.completedQuestionIds.filter(
          (questionId) => questionId !== "profile-priority",
        );
      reconcileIdentityState(state);
    },
    setOnboardingShopStatus: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      if (!isReachable(state, "shop-status")) {
        return;
      }

      const nextStatus = normalizeChoiceId<OnboardingShopStatusId>(
        action.payload,
        onboardingShopStatusIdSet,
      );
      if (nextStatus === state.questionFlow.shopStatus) {
        return;
      }

      state.questionFlow.shopStatus = nextStatus;
      invalidateFrom(state, "shop-status");
    },
    setSelectedProfileIds: (
      state,
      action: PayloadAction<readonly string[]>,
    ) => {
      const nextIds = normalizeOnboardingProfileSelection(
        action.payload,
        state.selectedProfileIds,
      );
      if (
        nextIds.join("\u0000") === state.selectedProfileIds.join("\u0000")
      ) {
        return;
      }

      state.selectedProfileIds = nextIds;
      state.primaryProfileId = nextIds.length === 1 ? (nextIds.at(0) ?? null) : null;
      reconcileIdentityState(state);
      if (nextIds.length > 1) {
        state.questionFlow.completedQuestionIds =
          state.questionFlow.completedQuestionIds.filter(
            (questionId) => questionId !== "profile-priority",
          );
      }
    },
    skipOnboardingProfileSelection: (state) => {
      state.selectedProfileIds = [];
      state.primaryProfileId = "undecided";
      state.questionFlow.a = createInitialAAnswers();
      state.questionFlow.b = createInitialBAnswers();
      state.questionFlow.c = createInitialCAnswers();
      reconcileIdentityState(state);
    },
  },
});

export const {
  completeOnboardingQuestion,
  hydrateOnboarding,
  resetOnboarding,
  setOnboardingAAdvantageIds,
  setOnboardingAOtherAdvantage,
  setOnboardingBAlertTiming,
  setOnboardingBGrowthWindow,
  setOnboardingBStartMethod,
  setOnboardingCAdvantageIds,
  setOnboardingCOtherAdvantage,
  setOnboardingCPriceRange,
  setOnboardingCPrimaryGoal,
  setOnboardingCProductStage,
  setOnboardingCategoryIds,
  setOnboardingDContentGoal,
  setOnboardingDEntryState,
  setOnboardingMarketIds,
  setOnboardingOtherCategory,
  setOnboardingPrimaryProfileId,
  setOnboardingShopStatus,
  setSelectedProfileIds,
  skipOnboardingProfileSelection,
} = onboardingSlice.actions;

export const selectOnboardingQuestionFlow = (state: RootState) =>
  state.onboarding.questionFlow;

export const selectOnboardingProfileIds = (state: RootState) =>
  state.onboarding.selectedProfileIds;

export const selectOnboardingPrimaryProfileId = (state: RootState) =>
  state.onboarding.primaryProfileId;

export default onboardingSlice.reducer;
